import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
// @ts-ignore
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface Company {
  id: string;
  name: string;
  type: string;
  aum: number;
  salary: number;
  bonus: number;
  carry: number;
  checked: boolean;
  grade: string;
}

interface FilteredBarChartProps {
  companies: Company[];
  selectedFilter: string;
  selectedAumFilter: string;
  selectedDataType: string;
  onFilterChange: (filter: string) => void;
  onAumFilterChange: (filter: string) => void;
  onDataTypeChange: (type: string) => void;
}

function getPercentile(arr: number[], percentile: number) {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const index = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[Math.max(0, index)];
}

const percentiles = [10, 25, 50, 75, 90];
const percentileLabels = ['10%', '25%', '50% (Медиана)', '75%', '90%'];

const FilteredBarChart: React.FC<FilteredBarChartProps> = ({ 
  companies, 
  selectedFilter, 
  selectedAumFilter, 
  selectedDataType,
  onFilterChange,
  onAumFilterChange,
  onDataTypeChange
}) => {
  // Фильтруем компании по выбранным фильтрам
  const getFilteredCompanies = () => {
    let filtered = companies;
    
    // Фильтр по типу
    switch (selectedFilter) {
      case 'pe':
        filtered = filtered.filter(company => company.type === 'PE');
        break;
      case 'fo':
        filtered = filtered.filter(company => company.type === 'Family Office');
        break;
      case 'all':
      default:
        break;
    }
    
    // Фильтр по AUM
    switch (selectedAumFilter) {
      case 'large':
        filtered = filtered.filter(company => company.aum >= 5);
        break;
      case 'medium':
        filtered = filtered.filter(company => company.aum >= 3 && company.aum < 5);
        break;
      case 'small':
        filtered = filtered.filter(company => company.aum >= 1 && company.aum < 3);
        break;
      case 'tiny':
        filtered = filtered.filter(company => company.aum < 1);
        break;
      case 'all':
      default:
        break;
    }
    
    return filtered;
  };

  const filteredCompanies = getFilteredCompanies();
  const dataValues = filteredCompanies.map(c => selectedDataType === 'salary' ? c.salary : c.bonus);

  const data = {
    labels: percentileLabels,
    datasets: [
      {
        label: selectedDataType === 'salary' ? 'Фиксированная оплата (₽)' : 'Переменная компенсация (%)',
        data: percentiles.map(p => getPercentile(dataValues, p)),
        backgroundColor: '#1a237e',
        borderRadius: 0,
        barThickness: 32,
      },
    ],
  };

  const formatValue = (value: number): string => {
    if (selectedDataType === 'bonus') {
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value) + '%';
    } else {
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value) + ' ₽';
    }
  };

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${formatValue(ctx.parsed.x)}`,
        },
      },
      datalabels: {
        display: true,
        color: '#ffffff',
        font: {
          size: 12,
          weight: 'bold' as const,
          family: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif"
        },
        formatter: (value: number) => formatValue(value),
        anchor: 'center' as const,
        align: 'center' as const,
        offset: 0
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          color: '#1a237e',
          font: { 
            size: 14,
            family: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif"
          },
          callback: (v: any) => selectedDataType === 'bonus' ? v + '%' : v.toLocaleString('ru-RU'),
        },
        grid: { color: 'rgba(26,35,126,0.08)' },
      },
      y: {
        ticks: { 
          color: '#1a237e', 
          font: { 
            size: 16,
            family: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif"
          } 
        },
        grid: { display: false },
      },
    },
  };

  return (
    <div style={{ width: '100%', maxWidth: 700, margin: '32px auto' }}>
      {/* Фильтры */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <select 
          style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif",
            fontSize: '14px',
            color: '#333',
            background: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '8px 12px',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
          value={selectedFilter}
          onChange={(e) => onFilterChange(e.target.value)}
        >
          <option value="all">PE&FO (Все компании)</option>
          <option value="pe">PE (только Private Equity)</option>
          <option value="fo">FO (только Family Office)</option>
        </select>
        
        <select 
          style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif",
            fontSize: '14px',
            color: '#333',
            background: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '8px 12px',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
          value={selectedAumFilter}
          onChange={(e) => onAumFilterChange(e.target.value)}
        >
                      <option value="all">Все по AUM $</option>
                    <option value="large">AUM &gt;= 5млрд$</option>
        <option value="medium">AUM 3-5млрд$</option>
        <option value="small">AUM 1-3млрд$</option>
        <option value="tiny">AUM &lt; 1млрд$</option>
        </select>
        
        <select 
          style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif",
            fontSize: '14px',
            color: '#333',
            background: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '8px 12px',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
          value={selectedDataType}
          onChange={(e) => onDataTypeChange(e.target.value)}
        >
          <option value="salary">Базовая заработная плата</option>
          <option value="bonus">Переменная компенсация</option>
        </select>
      </div>
      
      <div style={{
        textAlign: 'center',
        marginBottom: '16px',
        fontSize: '14px',
        color: '#666',
        fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif"
      }}>
        Выбрано компаний: <strong>{filteredCompanies.length}</strong>
      </div>
      
      <Bar data={data} options={options} />
    </div>
  );
};

export default FilteredBarChart; 